import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import FrontechLogo from './FrontechLogo';
import Seo from './marketing/Seo';

export default function Privacy() {
  return (
    <div style={{ background: 'var(--color-base)', color: 'var(--color-text-primary)', minHeight: '100vh' }}>
      <Seo
        title="Privacy Policy · FRONTECH"
        description="How FRONTECH handles your data. Applies to the frontech-consulting.com marketing site and the Front SaaS product."
        path="/privacy"
        noindex
      />
      <nav className="border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80">
            <FrontechLogo size={22} variant="icon" color="var(--color-accent)" />
            <span className="font-heading text-sm font-semibold">Front</span>
            <span className="text-xs font-mono" style={{ color: 'var(--color-text-muted)' }}>by FRONTECH</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm hover:opacity-80"
            style={{ color: 'var(--color-text-secondary)' }}>
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-5 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-2">Privacy Policy</h1>
        <p className="text-xs font-mono uppercase tracking-widest mb-10" style={{ color: 'var(--color-text-muted)' }}>
          Effective date: November 6, 2026
        </p>

        <div className="space-y-8 text-[15px] leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>1. Who we are</h2>
            <p>
              Front is a product of FRONTECH Consulting ("FRONTECH", "we", "us"), a SaaS company based in Pakistan
              providing conversational AI agents that can be embedded into websites, mobile apps, and other digital
              products. Contact: <span style={{ color: 'var(--color-accent)' }}>support@frontech-consulting.com</span>.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>2. What data we collect</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account data:</strong> email address, name, hashed password (handled by our auth provider Supabase).</li>
              <li><strong>Agent configuration:</strong> agent names, system prompts, voice/widget settings, knowledge-base documents you upload.</li>
              <li><strong>Usage data:</strong> voice minutes consumed, chat messages sent, conversation transcripts, conversation metadata (start/end time, duration).</li>
              <li><strong>Audio transcripts:</strong> when end users speak to your agent, audio is transcribed to text in real time and may be stored as part of the conversation log.</li>
              <li><strong>Billing data:</strong> handled by Paddle (see section 4). We receive your plan, subscription status, and the last 4 digits of the card on file — we never see your full card number.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>3. How we use it</h2>
            <p>
              We use your data to provide the service: hosting and routing your agents, generating responses,
              enforcing plan limits, displaying analytics in your dashboard, and contacting you about your account
              or material changes to the service. We do not sell your data, and we do not use the contents of your
              conversations to train general-purpose AI models.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>4. Third-party processors</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Supabase</strong> — database and authentication.</li>
              <li><strong>OpenAI</strong> — language model and text-to-speech. Per OpenAI's API policy, content sent through the API is not used to train their models.</li>
              <li><strong>Paddle</strong> — payment processing as Merchant of Record. All billing information is handled by Paddle on their infrastructure.</li>
              <li><strong>Railway / Netlify</strong> — hosting providers for the backend and frontend.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>5. Data retention and deletion</h2>
            <p>
              We retain account and configuration data for as long as your account is active. Conversation logs are
              retained for analytics and quality monitoring. You can request deletion of your account and all
              associated data by emailing <span style={{ color: 'var(--color-accent)' }}>support@frontech-consulting.com</span>.
              Deletion requests are processed within 30 days.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>6. Security</h2>
            <p>
              We use industry-standard practices: encrypted database connections, encrypted-at-rest storage,
              role-based access controls, and row-level security on tenant data. No system is perfectly secure;
              we will notify affected users without undue delay if we become aware of a breach.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>7. Your rights</h2>
            <p>
              You can access, correct, export, or delete your data at any time through the dashboard or by
              emailing support. Depending on your jurisdiction, you may have additional rights under GDPR,
              CCPA, or local privacy laws — we honor these rights regardless of where you are located.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>8. Children</h2>
            <p>
              Front is not directed at children under 13. We do not knowingly collect data from children. If you
              believe a child has provided us with personal information, contact us and we will delete it.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>9. Changes to this policy</h2>
            <p>
              We may update this policy from time to time. Material changes will be announced in-product or by
              email at least 14 days before they take effect. The effective date at the top of this page indicates
              when it was last revised.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>10. Contact</h2>
            <p>
              Questions, complaints, or data requests:{' '}
              <span style={{ color: 'var(--color-accent)' }}>support@frontech-consulting.com</span>.
              Jurisdiction: this policy is governed by the laws of the Islamic Republic of Pakistan.
            </p>
          </section>
        </div>
      </main>

      <footer className="py-10 px-5 mt-8" style={{ borderTop: '1px solid var(--color-border)' }}>
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            &copy; {new Date().getFullYear()} FRONTECH Consulting. All rights reserved.
          </span>
          <div className="flex items-center gap-5 text-xs">
            <Link to="/privacy" style={{ color: 'var(--color-text-muted)' }} className="hover:opacity-80">Privacy</Link>
            <Link to="/terms" style={{ color: 'var(--color-text-muted)' }} className="hover:opacity-80">Terms</Link>
            <Link to="/refunds" style={{ color: 'var(--color-text-muted)' }} className="hover:opacity-80">Refunds</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
